import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

type SidePanelProps = {
  side: 'left' | 'right';
  className: string;
  activeTabIndex: number;
  onOpen: () => void;
  onClose: () => void;
  onActiveTabIndexChange: (data: { activeTabIndex: number }) => void;
  isExpanded: boolean;
  panelOpen: boolean;
  expandedWidth?: number;
  collapsedWidth?: number;
  expandedInsideBorderSize?: number;
  collapsedInsideBorderSize?: number;
  collapsedOutsideBorderSize?: number;
  tabs: any[];
};

const baseClasses =
  'transition-all duration-300 ease-in-out justify-center box-content flex flex-col';

const SidePanel = ({
  className,
  activeTabIndex: activeTabIndexProp,
  panelOpen,
  tabs,
  onOpen,
  onClose,
  onActiveTabIndexChange,
  collapsedWidth = 5,
}: SidePanelProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(activeTabIndexProp ?? 0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = /(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const updateActiveTabIndex = useCallback(
    (newIndex: number, forceOpen = false) => {
      if (forceOpen) {
        onOpen();
      }
      setActiveTabIndex(newIndex);
      onActiveTabIndexChange({ activeTabIndex: newIndex });
    },
    [onActiveTabIndexChange, onOpen]
  );

  useEffect(() => {
    if (activeTabIndexProp !== null && activeTabIndexProp !== undefined) {
      setActiveTabIndex(activeTabIndexProp);
    }
  }, [activeTabIndexProp]);

  const style = {
    width: panelOpen ? (isMobile ? '99vw' : '88vw') : `${collapsedWidth}px`,
    position: 'absolute' as const,
    bottom: isMobile ? '8vh' : '38px',
    height: panelOpen ? (isMobile ? '135px' : 'auto') : 'auto',
    background: panelOpen ? 'rgba(0, 0, 0, 0.01)' : 'rgba(0, 0, 0, 0.01)',
    zIndex: 200,
    transition: 'all 0.3s ease-in-out',
    opacity: panelOpen ? 1 : 0.8,
  };

  return (
    <div
      className={classnames(className, baseClasses)}
      style={style}
    >
      {panelOpen &&
        tabs.map((tab, index) => (index === activeTabIndex ? <tab.content key={index} /> : null))}
    </div>
  );
};

export { SidePanel };
